<template>
  <!-- Render a button or an a tags based on href -->
  <component
    :is="componentType"
    :href="href ? href : null"
    :class="['ui-button',
      buttonType,
      appearance,
      size,
      {
        loading,
        disabled: isDisabled || currentLoading,
        block,
        inline
      },
      { 'horizontal-shake': animate === 'horizontal-shake' },
      { 'top-icon': iconPosition === 'top' },
      { 'normal-case': textNormal },
    ]"
    :disabled="isDisabled"
    :aria-label="ariaLabel || label"
    :aria-disabled="isDisabled"
    :type="type"
    @click="handleClick"
    @keydown.enter="handleClick"
    @keydown.space.prevent="handleClick"
    @animationend="handleAnimationEnd"
  >
    <!-- Icon before the label -->
    <slot name="icon-left">
      <span v-if="icon && iconPosition === 'left'" :class="['icon', icon]"></span>
    </slot>

    <!-- Icon top of the label -->
    <slot name="icon-top">
      <span v-if="icon && iconPosition === 'top'" :class="['icon', icon]"></span>
    </slot>

    <!-- Loader or label/slot content -->
    <span v-if="loading" class="loader" :class="loadingClass"></span>
    <span v-else class="ui-button__label" :class="labelClass">
      <slot>{{ label }}</slot>
    </span>

    <!-- Icon after the label -->
    <slot name="icon-right" class="icon-slot">
      <span v-if="icon && iconPosition === 'right'" :class="['icon', icon]"></span>
    </slot>
  </component>
</template>

<script>
// import Translations from "./insert-me-to-translate.vue";

import {
  buttonTypeValidator,
  buttonAppearanceValidator,
  buttonSizeValidator,
} from '../../utils';

export default {
  props: {
    href: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: '', // Default empty to support slot usage
    },
    size: {
      type: String,
      default: 'normal',
      validator: buttonSizeValidator,
    },
    buttonType: {
      type: String,
      default: 'fill',
      validator: buttonTypeValidator,
    },
    appearance: {
      type: String,
      default: 'primary',
      validator: buttonAppearanceValidator,
    },
    uppercase: {
      type: Boolean,
      default: false
    },
    capitalize: {
      type: Boolean,
      default: false
    },
    block: {
      type: Boolean,
      default: false,
    },
    inline: {
      type: Boolean,
      default: false,
    },
    fontWeight: {
      type: String,
      default: 'normal',
      validator: value => ['normal', 'light', 'medium', 'semibold', 'bold'].includes(value),
    },
    action: {
      type: String,
      required: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    ariaLabel: {
      type: String,
      default: null, // Optional aria-label for accessibility
    },
    type: {
      type: String,
      default: 'button',
      validator: value => ['button', 'submit', 'reset'].includes(value),
    },
    icon: {
      type: String,
      default: null,
    },
    iconPosition: {
      type: String,
      default: 'left',
      validator: value => ['left', 'right', 'top'].includes(value),
    },
    animate: {
      type: String,
      validator: value => [null, 'horizontal-shake'].includes(value),
    },
    normalCase:{
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      currentLoading: false,
      isAnimating: false,
    };
  },
  computed: {
    componentType() {
      return this.href ? 'a' : 'button';
    },
    labelClass() {
      return {
        'font-weight-light': this.fontWeight === 'light',
        'font-weight-normal': this.fontWeight === 'normal',
        'font-weight-medium': this.fontWeight === 'medium',
        'font-weight-semibold': this.fontWeight === 'semibold',
        'font-weight-bold': this.fontWeight === 'bold',
        'text-uppercase': this.uppercase,
        'text-capitalize': this.capitalize,
      }
    },
    loadingClass() {
      return {
        'loading--primary': this.appearance === 'primary',
        'loading--secondary': this.appearance === 'secondary',
        'loading--tertiary': this.appearance === 'tertiary',
      }
    },
    isSubmit() {
      return this.type === 'submit';
    },
    isDisabled() {
      if(this.href) {
        return this.currentLoading || this.disabled;
      }

      return this.disabled;
    }
  },
  methods: {
    handleClick(e) {
      // Prevent default if no href and not a submit button
      if (!this.href && !this.isSubmit) {
        e.preventDefault();
      }

      // Set loading state only if there's a link or submission
      // This disable button/link after click
      if (this.href || this.isSubmit) {
        this.currentLoading = true;
      }

      // Emit click only if not loading or disabled
      if (!this.loading && !this.isDisabled) {
        this.$emit("click", e);
      }
    },
    handleAnimationEnd(event) {
      if (event.animationName === 'horizontal-shaking') {
        this.isAnimating = false;
        this.$emit('update:animateButton', false);
      }
    },

  },
  watch: {
    animateButton(newVal) {
      if (newVal) {
        this.isAnimating = true;
      }
    },
  },
};
</script>
